<template>
  <div class="p-1">
    <v-card class="rounded-xl border border-slate-100 shadow-sm overflow-hidden" flat>
      <v-card-text class="p-6">
        <div class="flex flex-col gap-6">
          <!-- Title & Description Header -->
          <div class="flex items-start gap-4">
            <v-avatar color="indigo lighten-5" class="p-1" size="48">
              <v-icon color="indigo" size="28">mdi-email-outline</v-icon>
            </v-avatar>
            <div class="flex flex-col">
              <span class="text-sm font-bold text-slate-800 leading-tight">
                Mail Forwarding Integration
              </span>
              <span class="text-xs text-slate-500 mt-1 leading-normal">
                Map specific Use Cases and Sub Cases to target email addresses for automated email
                forwarding.
              </span>
            </div>
          </div>

          <v-divider class="border-slate-100"></v-divider>

          <!-- Configuration Form -->
          <div v-if="loading" class="py-12 text-center">
            <v-progress-circular indeterminate color="indigo" size="32"></v-progress-circular>
            <div class="text-slate-400 mt-2 font-medium text-xs">
              Loading integration settings...
            </div>
          </div>

          <div v-else class="flex flex-col gap-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Use Case Selection -->
              <div>
                <label class="text-xs font-bold text-slate-700 mb-1 block">Use Case *</label>
                <v-select
                  v-model="config.useCase"
                  :items="useCaseOptions"
                  placeholder="Select Use Case"
                  outlined
                  dense
                  class="rounded-lg text-xs"
                  hide-details="auto"
                  @change="onUseCaseChange"
                ></v-select>
              </div>

              <!-- Sub Case Selection -->
              <div>
                <label class="text-xs font-bold text-slate-700 mb-1 block">Sub Case *</label>
                <v-select
                  v-model="config.subCase"
                  :items="filteredSubCases"
                  placeholder="Select Sub Case"
                  outlined
                  dense
                  class="rounded-lg text-xs"
                  hide-details="auto"
                  :disabled="!config.useCase"
                  @change="onSubCaseChange"
                ></v-select>
              </div>

              <!-- Autogenerated Forwarding Email Field (Display only) -->
              <div class="md:col-span-2">
                <label class="text-xs font-bold text-slate-700 mb-1 block"
                  >Forwarding Email Address</label
                >
                <v-text-field
                  :value="autogeneratedEmail"
                  readonly
                  outlined
                  dense
                  filled
                  background-color="grey lighten-4"
                  class="rounded-lg text-xs"
                  hide-details="auto"
                  prepend-inner-icon="mdi-email-outline"
                ></v-text-field>
              </div>
            </div>

            <!-- Dynamic Field Mappings based on selected subcase -->
            <div
              v-if="activeUseCase && mappableFields.length"
              class="border border-slate-100 rounded-xl p-4 bg-slate-50 mt-2"
            >
              <div class="text-xs font-bold text-slate-800 mb-3 flex items-center gap-2">
                <v-icon size="16" color="indigo">mdi-vector-link</v-icon>
                Inbound Case Field Mapping
              </div>

              <div class="flex flex-col gap-4">
                <div
                  v-for="field in mappableFields"
                  :key="field.key"
                  class="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center border-b border-slate-100 pb-3 last:border-0 last:pb-0"
                >
                  <div>
                    <div class="text-xs font-semibold text-slate-800">
                      {{ field.label }}
                    </div>
                    <span class="text-[9px] text-slate-400 font-medium uppercase tracking-wider">{{
                      field.group
                    }}</span>
                  </div>
                  <div>
                    <v-select
                      v-model="config.fieldMapping[field.key]"
                      :items="webhookFieldOptions"
                      placeholder="Select Webhook Field (Optional)"
                      outlined
                      dense
                      hide-details="auto"
                      clearable
                      class="rounded-lg text-xs bg-white"
                    ></v-select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Auto Reply Templates configuration -->
            <div
              v-if="config.subCase"
              class="border border-slate-100 rounded-xl p-4 bg-slate-50 mt-2"
            >
              <div class="text-xs font-bold text-slate-800 mb-3 flex items-center gap-2">
                <v-icon size="16" color="indigo">mdi-reply-all-outline</v-icon>
                Auto Reply Notifications (Case Creation)
              </div>
              <div class="w-full">
                <!-- Email Auto Reply -->
                <div class="w-full">
                  <label class="text-xs font-bold text-slate-700 mb-1 block"
                    >Email Auto Reply Template</label
                  >
                  <v-select
                    v-model="config.autoReplyEmailTemplate"
                    :items="emailTemplates"
                    placeholder="Select Email Template"
                    outlined
                    dense
                    clearable
                    class="rounded-lg text-xs bg-white w-full"
                    hide-details="auto"
                  ></v-select>
                </div>
              </div>
            </div>

            <!-- Save Action Button -->
            <div class="flex justify-end gap-2 mt-4">
              <v-btn
                color="indigo"
                class="normal-case rounded-lg text-xs white--text"
                depressed
                @click="saveConfig"
                :loading="saving"
                :disabled="!config.useCase || !config.subCase"
              >
                <v-icon left size="16">mdi-content-save-outline</v-icon>
                Save
              </v-btn>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'MailForwardingIntegration',
  props: {
    receivedData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      subCasesList: [],
      useCaseOptionsList: [],
      templatesList: [],
      config: {
        useCase: '',
        subCase: '',
        fieldMapping: {},
        autoReplyEmailTemplate: null
      },
      webhookFieldOptions: [
        { text: 'Email Subject', value: 'subject' },
        { text: 'Email Body', value: 'message_body' },
        { text: 'Sender Email', value: 'sender_email' },
        { text: 'Recipient Email', value: 'recipient_email' },
        { text: 'Attachments', value: 'attachments' },
        { text: 'Message ID', value: 'aws_message_id' }
      ]
    }
  },
  computed: {
    useCaseOptions() {
      return this.useCaseOptionsList
    },
    filteredSubCases() {
      if (!this.config.useCase) return []
      return this.subCasesList
        .filter((sc) => String(sc.useCase).toUpperCase() === this.config.useCase)
        .map((sc) => ({
          text: sc.name,
          value: sc._id
        }))
    },
    autogeneratedEmail() {
      const orgData = this.$storage.getUniversal('orgData')
      const orgId = orgData?.id || ''
      return `complaints+${orgId}@inbound.myfinfi.com`
    },
    activeUseCase() {
      if (!this.config.subCase) return ''
      const sub = this.subCasesList.find((s) => s._id === this.config.subCase)
      return sub ? String(sub.useCase).toLowerCase() : ''
    },
    emailTemplates() {
      return this.templatesList
        .filter((t) => t.type === 'EMAIL' && t.module === 'other')
        .map((t) => ({ text: t.name, value: t._id }))
    },
    mappableFields() {
      if (!this.config.subCase) return []
      const sub = this.subCasesList.find((s) => s._id === this.config.subCase)
      if (!sub) return []

      const list = []

      // 1. Additional Columns (Custom fields)
      if (sub.additionalColumns) {
        const cols = sub.additionalColumns
        Object.keys(cols).forEach((k) => {
          const col = cols[k]
          if (col && col.displayName) {
            list.push({
              key: `additionalColumns.${col.key || k}`,
              label: `Custom: ${col.displayName}`,
              group: 'Custom Fields (Additional Columns)'
            })
          }
        })
      }

      return list
    }
  },
  async mounted() {
    await this.fetchAllowedUseCases()
    await this.fetchSubCases()
    await this.fetchTemplates()
    await this.loadSavedConfig()
  },
  methods: {
    async fetchAllowedUseCases() {
      try {
        const res = await this.$axios.get(`/orgSetup/allowed-use-cases`)
        const list = res.data?.data || []
        this.useCaseOptionsList = list
          .filter((u) => u.useCase.toUpperCase() === 'BASE')
          .map((u) => ({
            text: u.navName || 'Base Management',
            value: u.useCase.toUpperCase()
          }))
      } catch (error) {
        console.error('Error fetching allowed use cases:', error)
      }
    },
    async fetchSubCases() {
      this.loading = true
      try {
        const response = await this.$axios.get('/orgSetup/extend-usecase')
        this.subCasesList = response.data?.data || []
      } catch (error) {
        console.error('Error fetching sub cases:', error)
        this.$toast.error('Failed to load sub cases')
      } finally {
        this.loading = false
      }
    },
    async fetchTemplates() {
      try {
        const response = await this.$axios.get('/letter-templates?limit=*')
        this.templatesList = response.data?.data || []
      } catch (error) {
        console.error('Error fetching templates:', error)
      }
    },
    async loadSavedConfig() {
      const saved = this.receivedData?.applications?.mailForwarding || {}
      this.config = {
        useCase: saved.useCase || '',
        subCase: saved.subCase || '',
        fieldMapping: saved.fieldMapping || {},
        autoReplyEmailTemplate: saved.autoReplyEmailTemplate || null
      }
      this.initializeFieldMapping()
    },
    onUseCaseChange() {
      this.config.subCase = ''
      this.config.fieldMapping = {}
    },
    onSubCaseChange() {
      this.initializeFieldMapping()
    },
    initializeFieldMapping() {
      if (!this.config.fieldMapping) {
        this.$set(this.config, 'fieldMapping', {})
      }
      this.mappableFields.forEach((field) => {
        if (this.config.fieldMapping[field.key] === undefined) {
          this.$set(this.config.fieldMapping, field.key, null)
        }
      })
    },
    async saveConfig() {
      this.saving = true
      try {
        const payload = {
          applications: {
            ...this.receivedData?.applications,
            mailForwarding: {
              ...this.receivedData?.applications?.mailForwarding,
              enable: true,
              useCase: this.config.useCase,
              subCase: this.config.subCase,
              fieldMapping: this.config.fieldMapping,
              autoReplyEmailTemplate: this.config.autoReplyEmailTemplate
            }
          }
        }
        await this.$axios.post('/org/config', payload)
        this.$emit('refetchData')
        this.$toast.success('Mail Forwarding Integration configured successfully')
      } catch (error) {
        console.error('Error saving mail forwarding configuration:', error)
        this.$toast.error('Failed to save configuration settings')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
